// Vortex Secure - Login Page Component
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { signInWithProvider, signInWithPassword, signUpWithPassword } from '../../lib/supabase';
import { Shield, Github, Mail, Chrome } from 'lucide-react';

export function LoginPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);

  const handleOAuthLogin = async (provider: 'github' | 'google') => {
    try {
      setLoading(provider);
      await signInWithProvider(provider);
    } catch (error) {
      console.error('OAuth login failed:', error);
      alert('Login failed. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading('email');
      if (isSignUp) {
        await signUpWithPassword(email, password);
      } else {
        await signInWithPassword(email, password);
      }
      // No manual navigation needed: App.tsx's onAuthStateChange listener
      // picks up the new session and re-renders into the logged-in view.
    } catch (error) {
      console.error('Email login failed:', error);
      const message = error instanceof Error ? error.message : 'Login failed. Please try again.';
      alert(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Vortex Secure</h1>
          <p className="text-gray-600 mt-2">Admin Dashboard</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-center">
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* OAuth Buttons */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthLogin('github')}
                disabled={loading === 'github'}
              >
                {loading === 'github' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                ) : (
                  <Github className="h-4 w-4 mr-2" />
                )}
                Continue with GitHub
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthLogin('google')}
                disabled={loading === 'google'}
              >
                {loading === 'google' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                ) : (
                  <Chrome className="h-4 w-4 mr-2" />
                )}
                Continue with Google
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or continue with email</span>
              </div>
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={loading === 'email'}
              >
                {loading === 'email' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                {isSignUp ? 'Create Account' : 'Sign In'}
              </Button>
            </form>

            {/* Toggle Sign Up / Sign In */}
            <div className="text-center">
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp 
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Sign up"
                }
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Demo Credentials */}
        <Card className="mt-6 bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <h3 className="font-medium text-yellow-800 mb-2">Demo Access</h3>
            <p className="text-sm text-yellow-700 mb-3">
              For testing purposes, use these demo credentials:
            </p>
            <div className="space-y-1 text-sm font-mono">
              <div>Email: <span className="text-yellow-800">admin@vortex-secure.demo</span></div>
              <div>Password: <span className="text-yellow-800">demo123456</span></div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full text-yellow-800 border-yellow-300 hover:bg-yellow-100"
              onClick={() => {
                setEmail('admin@vortex-secure.demo');
                setPassword('demo123456');
              }}
            >
              Fill Demo Credentials
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}